"use client"

import { Suspense } from "react"
import Script from "next/script"

import { isMetaPixelEnabled, META_PIXEL_ID } from "@/lib/analytics/enabled"
import { flushMetaPixelQueue } from "@/lib/analytics/meta-pixel"

import { MetaPixelPageView } from "./MetaPixelPageView"

/**
 * Meta Pixel — the whole installation, mounted once from the root layout.
 *
 * The loader is `/scripts/pixel.js`, a static same-origin file, not an inline
 * snippet: the id travels as `data-pixel-id`, and a throw inside our own ~15
 * lines arrives with a real stack instead of a bare `Script error.`
 * (`connect.facebook.net` serves no `Access-Control-Allow-Origin`, so
 * `crossOrigin="anonymous"` is not an option — it would fail the request
 * outright. `fbevents.js` itself therefore stays opaque.)
 *
 * `afterInteractive` keeps the loader off the critical render path. What that
 * costs is a window where `window.fbq` does not exist yet, which the queue in
 * `@/lib/analytics/meta-pixel` covers; `onLoad` drains it.
 */
export function MetaPixel() {
  if (!isMetaPixelEnabled()) return null
  const pixelId = META_PIXEL_ID as string

  return (
    <>
      <Script
        id="meta-pixel"
        src="/scripts/pixel.js"
        strategy="afterInteractive"
        data-pixel-id={pixelId}
        onLoad={flushMetaPixelQueue}
      />
      <noscript>
        {/* next/image cannot be used inside <noscript> — this is a tracking
            beacon, not an image. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          alt=""
          src={`https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`}
        />
      </noscript>
      <Suspense fallback={null}>
        <MetaPixelPageView />
      </Suspense>
    </>
  )
}
